import React, { ReactNode, useMemo, useState } from "react";
import gql from "graphql-tag";
import { Box, Grid2 } from "@mui/material";
import FileUploadModal from "../../../../copiedLP/src/pages/CreditSubmissions/DecisionFileUploadModal";
import { useMutation, useQuery } from "@apollo/client";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import { AddOutlined } from "@mui/icons-material";
import { SET_CS_REFETCH } from "../../../../redux/types";
import { DeclineForm } from "./DeclineForm";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { CurrencyInputField, InputField, NumberFormatField, SelectField } from "components/form";
import {
  CreditSubmissionDeclindeReasonsResponse,
  DecisionCardFormValues,
  DeclineReasonOption,
} from "modules/creditSubmission/types";
import { ModalsKeys } from "global";
import { AddEditDeclineReasonDialog } from "../dialogs";
import { ModalsContainer } from "components/shared/Modals";
import { GET_CREDIT_SUBMISSION_DECLINE_REASONS } from "modules/creditSubmission/api/getCreditSubmissionDeclineReason";
import { getSubmissionDeclineReasonsOptions } from "modules/creditSubmission/lib";

interface DecisionCardProps {
  refetch: () => void;
  cs: {
    creditSubId: number | string;
    approvedAmount?: number;
    approvedRate?: number;
    decision?: string;
    notes?: string;
    contingencies?: string;
    masterCreditSubmissionId?: string | null;
    creditSubmissionDeclineReasonId?: string | null;
  };
  handleOpenModal: () => Promise<void>;
  handleSubmissionMessage: (msg: { title: string; message: string }) => void;
}

const POST_CREDIT_DECISION = gql`
  mutation ($entityId: String!, $fieldsObj: LenderCreditDecision) {
    postLenderCreditDecision(entityId: $entityId, fieldsObj: $fieldsObj)
  }
`;

const decisionOptions = [
  { value: 803370000, label: "Awaiting Review" },
  { value: 803370001, label: "Requested Additional Information" },
  { value: 803370004, label: "Pre-Approved" },
  { value: 803370002, label: "Declined" },
  { value: 803370003, label: "Approved" },
];

export const DecisionCard = (props: DecisionCardProps) => {
  const { refetch } = props;

  const dispatch = useDispatch();
  const account = useSelector((state: any) => state.account);
  const refetchCS = useSelector((state: any) => state.cs.refetch);

  const creditId = props.cs.creditSubId.toString();

  const [showContingencies, setShowContingencies] = useState(false);
  const [declineReasonOption, setDeclineReasonOption] = useState<DeclineReasonOption | null>();

  const { data: creditSubmissionDeclineReasonsData, refetch: refetchCreditSubmissionDeclineReasons } =
    useQuery<CreditSubmissionDeclindeReasonsResponse>(GET_CREDIT_SUBMISSION_DECLINE_REASONS, {
      variables: { accountId: account.id },
      onCompleted: data => {
        if (props.cs.creditSubmissionDeclineReasonId) {
          const declineReasonOption =
            getSubmissionDeclineReasonsOptions(data?.creditSubmissionDeclineReasons || []).find(
              csdr => csdr.value === props.cs.creditSubmissionDeclineReasonId
            ) || null;
          setDeclineReasonOption(declineReasonOption);
          form.setValue("creditSubmissionDeclineReason", declineReasonOption);
        }
      },
    });

  const [postCreditDecision] = useMutation(POST_CREDIT_DECISION);

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.AddEditDeclineReasonDialog]: <AddEditDeclineReasonDialog />,
  };

  const { submissionDecision, submissionApprovedAmount, submissionRate, submissionNotes, submissionContingencies } =
    useMemo(
      () => ({
        submissionDecision: decisionOptions.find(o => o.label === props.cs.decision)?.value ?? null,
        submissionApprovedAmount: props.cs.approvedAmount ? props.cs.approvedAmount : undefined,
        submissionRate: props.cs.approvedRate ?? undefined,
        submissionNotes: props.cs.notes || "",
        submissionContingencies: props.cs.contingencies || "",
      }),
      [props.cs]
    );

  const form = useForm<DecisionCardFormValues>({
    defaultValues: {
      decision: submissionDecision,
      approvedAmount: submissionApprovedAmount,
      rate: submissionRate,
      notes: submissionNotes,
      contingencies: submissionContingencies,
      creditSubmissionDeclineReason: "",
    },
  });

  const decision = useWatch({ control: form.control, name: "decision" });

  const submit = async (formValues: DecisionCardFormValues) => {
    try {
      const payload = {
        decision: formValues.decision,
        approvedAmount: formValues.approvedAmount,
        rate: formValues.rate,
        notes: formValues.notes,
        contingencies: formValues.contingencies,
        masterCreditSubmissionId:
          !!props.cs.masterCreditSubmissionId && props.cs.masterCreditSubmissionId
            ? props.cs.masterCreditSubmissionId
            : null,
        creditSubmissionDeclineReasonId:
          (formValues.creditSubmissionDeclineReason as DeclineReasonOption)?.value || null,
      };

      props.handleOpenModal();
      props.handleSubmissionMessage({
        title: "Sending",
        message: "Please wait. Submitting your request to lenders...",
      });

      await postCreditDecision({
        variables: {
          entityId: creditId,
          fieldsObj: payload,
        },
      });
      props.handleSubmissionMessage({
        title: "Success",
        message: "Submitted Successfully",
      });
      refetch();
      dispatch({
        type: SET_CS_REFETCH,
        payload: { refetch: !refetchCS },
      });
    } catch (err) {
      console.log(err);
      props.handleSubmissionMessage({
        title: "Error",
        message: "There was something wrong with your request",
      });
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <Box /* className={classes.decisionDropdown} */ mb={2}>
          <SelectField
            name="decision"
            label="Decision"
            control={form.control}
            options={decisionOptions}
            required
            extraAction={newValue => {
              form.reset({
                decision: newValue as number,
                approvedAmount: submissionApprovedAmount,
                rate: submissionRate,
                notes: submissionNotes,
                contingencies: submissionContingencies,
                creditSubmissionDeclineReason: declineReasonOption,
              });
            }}
          />
        </Box>

        {!!decision && (
          <>
            <Grid2 container spacing={2}>
              {decision === 803370003 && (
                <>
                  <Grid2 size={6}>
                    <CurrencyInputField
                      control={form.control}
                      label="Approved Amount"
                      name="approvedAmount"
                      rules={{ required: true }}
                      textFieldProps={{ required: true }}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <NumberFormatField
                      decimalScale={2}
                      control={form.control}
                      name="rate"
                      label="Rate"
                      rules={{ required: true }}
                      textFieldProps={{ fullWidth: true, required: true }}
                    />
                  </Grid2>
                </>
              )}

              {decision !== 803370000 && decision !== 803370002 && (
                <Grid2 size={12}>
                  <InputField
                    name="notes"
                    label={decision === 803370003 ? "Notes" : "Request"}
                    control={form.control}
                    rules={{ required: true }}
                    inputProps={{ multiline: true, fullWidth: true, rows: 7, variant: "outlined", required: true }}
                  />
                </Grid2>
              )}

              {decision === 803370002 && creditSubmissionDeclineReasonsData?.creditSubmissionDeclineReasons?.length && (
                <Grid2 size={12}>
                  <DeclineForm<DecisionCardFormValues>
                    isEditable={true}
                    creditSubmissionDeclineReasons={creditSubmissionDeclineReasonsData?.creditSubmissionDeclineReasons!}
                    refetchCreditSubmissionDeclineReasons={refetchCreditSubmissionDeclineReasons}
                  />
                </Grid2>
              )}

              {decision === 803370003 && (
                <>
                  {!showContingencies ? (
                    <>
                      <Grid2>
                        <Button onClick={() => setShowContingencies(true)}>
                          <AddOutlined /> Add Contingencies
                        </Button>
                      </Grid2>
                      <Grid2>
                        <FileUploadModal cs={props.cs} />
                      </Grid2>
                    </>
                  ) : (
                    <>
                      <Grid2 size={12}>
                        <InputField
                          name="contingencies"
                          label="Contingencies (if applicable)"
                          control={form.control}
                          inputProps={{
                            multiline: true,
                            rows: 7,
                            variant: "outlined",
                          }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <Button onClick={() => setShowContingencies(false)} variant="contained">
                          Cancel
                        </Button>
                      </Grid2>
                    </>
                  )}
                </>
              )}
            </Grid2>

            {decision !== null && (
              <Grid2 container style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "24px" }}>
                <Button
                  onClick={() => {
                    setShowContingencies(false);
                  }}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit" onClick={form.handleSubmit(submit)}>
                  Submit
                </Button>
              </Grid2>
            )}
          </>
        )}
      </FormProvider>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};
